import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { DemoHint } from "@/components/demo/DemoHint";
import { LoadsTableV2 } from "@/components/dashboard/LoadsTableV2";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useMyLoads } from "@/hooks/useMyLoads";
import { supabase } from "@/integrations/supabase/client";
import BrokerOffersSection from "@/components/broker-offers/BrokerOffersSection";
import { useAuth } from "@/contexts/AuthContext";

type MyLoadsTab = "loads" | "offers";

export default function MyLoadsPageV2() {
  usePageMeta({ title: "My Loads | LaneRunner", description: "Manage your posted loads" });

  const { profile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "offers" ? "offers" : "loads";
  const [activeTab, setActiveTab] = useState<MyLoadsTab>(initialTab);

  const { loads, loading, refetch } = useMyLoads();
  const [viewCounts, setViewCounts] = useState<Record<string, number>>({});

  const isBrokerOrShipper =
    profile?.user_type === "broker" || profile?.user_type === "shipper";

  // Sync tab from URL changes
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "offers" && isBrokerOrShipper) setActiveTab("offers");
    else if (!tab || tab === "loads") setActiveTab("loads");
  }, [searchParams, isBrokerOrShipper]);

  const handleTabChange = (tab: MyLoadsTab) => {
    setActiveTab(tab);
    if (tab === "offers") {
      setSearchParams({ tab: "offers" });
    } else {
      setSearchParams({});
    }
  };

  useEffect(() => {
    if (loads.length === 0) {
      setViewCounts({});
      return;
    }

    const loadIds = loads.map(l => l.id);
    supabase.rpc('get_load_view_counts', { load_ids: loadIds }).then(({ data }) => {
      if (data) {
        const counts: Record<string, number> = {};
        for (const row of data) {
          counts[row.load_id] = Number(row.view_count);
        }
        setViewCounts(counts);
      }
    });
  }, [loads]);

  return (
    <div className="h-full flex flex-col">
      <div className="px-3 py-2 xl:px-4 xl:py-3 border-b border-border bg-card flex items-center justify-between">
        <div className="flex items-center gap-4">
          <DemoHint hintKey="page.myLoads" side="bottom" showIndicator>
            <h1 className="text-lg xl:text-xl font-bold text-foreground">My Loads</h1>
          </DemoHint>
          {isBrokerOrShipper && (
            <div className="flex items-center gap-0 border border-border rounded-md overflow-hidden">
              <button
                onClick={() => handleTabChange("loads")}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  activeTab === "loads"
                    ? "bg-primary text-primary-foreground"
                    : "bg-card text-muted-foreground hover:bg-muted"
                }`}
              >
                MY LOADS
              </button>
              <button
                onClick={() => handleTabChange("offers")}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  activeTab === "offers"
                    ? "bg-primary text-primary-foreground"
                    : "bg-card text-muted-foreground hover:bg-muted"
                }`}
              >
                OFFERS
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="flex-1 p-3 xl:p-4">
        {activeTab === "offers" && isBrokerOrShipper ? (
          <BrokerOffersSection />
        ) : (
          <LoadsTableV2 loads={loads} loading={loading} refetch={refetch} viewCounts={viewCounts} maxRows={21} />
        )}
      </div>
    </div>
  );
}
